import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Venue, VenueListScope } from '@/types/venue';
import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { fetchVenues, deleteVenue } from '@/api/venueApi';
import VenueList from '@/components/VenueList';
import VenueMap from '@/components/VenueMap';
import VenueFormModal from '@/components/VenueFormModal';
import VenueMapPreview from '@/components/tickets/VenueMapPreview';
import { addToast } from '@heroui/toast';
import { IconPlus as Plus, IconSearch as Search, IconX as X } from '@tabler/icons-react';
import { sectionTitle, btnAccent } from '@/components/ui/design';

const pagerBtn = 'inline-flex h-11 items-center justify-center rounded-full px-5 text-sm font-semibold text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]';

const VenueManagementScreen: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [onlyMine, setOnlyMine] = useState(false);
    const [previewVenue, setPreviewVenue] = useState<Venue | null>(null);
    const { selectedOrganizerId } = useOrganization();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const isSiteAdmin = Boolean(user?.roles?.includes('admin'));
    const scope: VenueListScope = onlyMine ? 'mine' : isSiteAdmin ? 'manage' : 'available';

    const { data: venuesData, isLoading, error } = useQuery({
        queryKey: ['venues', page, searchTerm, scope, selectedOrganizerId],
        queryFn: () => fetchVenues({ page, limit: 10, search: searchTerm, scope }),
    });

    const deleteVenueMutation = useMutation({
        mutationFn: deleteVenue,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['venues'] });
            addToast({ title: 'Success', description: 'Venue deleted successfully.', color: 'success' });
        },
        onError: (err: any) => {
            addToast({
                title: 'Error',
                description: err?.response?.data?.message || err?.message || 'Failed to delete venue. Please try again.',
                color: 'danger',
            });
        },
    });

    const handleDeleteVenue = (id: string) => {
        if (window.confirm('Are you sure you want to delete this venue?')) {
            deleteVenueMutation.mutate(id);
        }
    };

    const handleEditVenue = (venue: Venue) => {
        setEditingVenue(venue);
        setIsFormOpen(true);
    };

    const handleCreateNew = () => {
        setEditingVenue(null);
        setIsFormOpen(true);
    };

    if (error) {
        return (
            <div className="w-full flex justify-center items-center rounded-[28px] bg-grey99 px-6 py-16 text-center text-red-500 dark:bg-white/[0.04] dark:text-red-400 text-lg">
                Failed to load venues. Please try again.
            </div>
        );
    }

    const venues = venuesData?.data || [];

    return (
        <div className="w-full mx-auto pb-8">
            <h2 className={`${sectionTitle} mb-6`}>Venues</h2>
            <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
                <div className="relative w-full md:max-w-[352px]">
                    <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-grey60 dark:text-nightMuted w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search venues"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                        className="w-full h-12 pl-11 pr-4 rounded-full bg-grey99 text-base text-grey10 ring-1 ring-black/[0.04] placeholder:text-grey60 outline-none transition-shadow focus:ring-2 focus:ring-primary/50 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:placeholder:text-nightMuted dark:focus:ring-nightAccent/60"
                    />
                </div>
                <div className="flex items-center justify-between gap-3">
                <div className="inline-flex items-center p-1 rounded-full bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06] flex-shrink-0" role="tablist" aria-label="Venue filter">
                    {[{ mine: false, label: 'All venues' }, { mine: true, label: 'My venues' }].map(tab => (
                        <button
                            key={tab.label}
                            role="tab"
                            aria-selected={onlyMine === tab.mine}
                            onClick={() => { setOnlyMine(tab.mine); setPage(1); setSelectedId(null); }}
                            className={`h-9 rounded-full px-4 text-sm font-semibold whitespace-nowrap transition-all ${onlyMine === tab.mine ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg' : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <button
                    className={`${btnAccent} h-11 px-5 flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-50`}
                    onClick={handleCreateNew}
                    disabled={!selectedOrganizerId}
                    title={selectedOrganizerId ? undefined : 'Select an organization to add a venue'}
                >
                    <Plus className="w-4 h-4" />
                    Add Venue
                </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-5 items-start">
                <div className="order-2 lg:order-1">
                    <VenueList
                        venues={venues}
                        isLoading={isLoading}
                        selectedId={selectedId}
                        onSelect={(id) => setSelectedId(prev => (prev === id ? null : id))}
                        onEdit={handleEditVenue}
                        onDelete={handleDeleteVenue}
                        onPreview={setPreviewVenue}
                    />
                </div>

                <div className="order-1 lg:order-2 lg:sticky lg:top-24">
                    <VenueMap venues={venues} selectedId={selectedId} />
                </div>
            </div>

            {venuesData && venuesData.pagination.totalPages > 1 && (
                <div className="mt-8 flex justify-center">
                    <div className="flex items-center gap-2">
                        <button
                            className={pagerBtn}
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            Previous
                        </button>
                        <span className="px-3 text-sm font-semibold text-grey40 dark:text-nightMuted">
                            Page {page} of {venuesData.pagination.totalPages}
                        </span>
                        <button
                            className={pagerBtn}
                            disabled={page === venuesData.pagination.totalPages}
                            onClick={() => setPage(page + 1)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {previewVenue && (
                <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/50 p-4" onMouseDown={e => e.target === e.currentTarget && setPreviewVenue(null)}>
                    <div role="dialog" aria-modal="true" aria-label={`${previewVenue.name} seat map preview`} className="w-full max-w-2xl rounded-[28px] bg-white p-4 shadow-2xl dark:bg-nightSurface sm:p-6">
                        <div className="mb-2 flex justify-end">
                            <button type="button" onClick={() => setPreviewVenue(null)} aria-label="Close" className="h-10 w-10 flex items-center justify-center rounded-full text-grey20 ring-1 ring-grey90 hover:bg-grey99 dark:text-nightMuted dark:ring-white/15 dark:hover:bg-white/10">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <VenueMapPreview
                            venueName={previewVenue.name}
                            seatMap={previewVenue.seatMap}
                            hasVenue
                            isLoading={false}
                            isError={false}
                            onRetry={() => undefined}
                            canEditVenue={false}
                            onEditVenue={() => undefined}
                            onSelectVenue={() => setPreviewVenue(null)}
                        />
                    </div>
                </div>
            )}

            {isFormOpen && (
                <VenueFormModal
                    isOpen={isFormOpen}
                    venue={editingVenue}
                    onClose={() => setIsFormOpen(false)}
                />
            )}
        </div>
    );
};

export default VenueManagementScreen;
